import { Component, OnDestroy, OnInit } from '@angular/core';

import { ActivatedRoute } from '@angular/router';

import { DataSourceType } from 'app/models';
import { PinningService } from 'app/modules/dashboard/services/pinning.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Dashboard, DashboardService } from 'app/providers/bridge-api/dashboard.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { DocumentService } from 'app/providers/storage-api/document.service';
import { Project, ProjectService } from 'app/providers/storage-api/project.service';
import { DocHighlightService } from 'app/services/doc-highlight.service';
import { LoggerService } from 'app/services/logger.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { DocumentViewOptions } from '../document-view/document-view.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-document-view-window',
  templateUrl: './document-view-window.component.html',
  styleUrls: ['./document-view-window.component.scss'],
  standalone: false,
})
export class DocumentViewWindowComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  options: DocumentViewOptions;

  groupId: string;
  projectId: string;
  dashboardId: string;

  constructor(
    private route: ActivatedRoute,
    private dashboardService: DashboardService,
    private groupService: GroupService,
    private projectService: ProjectService,
    private documentService: DocumentService,
    private authService: AuthService,
    private pinningService: PinningService,
    private logger: LoggerService,
    private channelService: DocHighlightService
  ) {}

  async ngOnInit() {
    const params = this.route.snapshot.paramMap;

    this.groupId = params.get('groupId');
    this.projectId = params.get('projectId');
    this.dashboardId = params.get('dashboardId');
    const collectionName = params.get('collectionName');
    const documentId = params.get('documentId');

    await this.groupService.refreshAndSelectActiveGroup(this.groupId);

    const queryParams = this.route.snapshot.queryParamMap;

    const entity = queryParams.get('entity');
    const entityDisplayName = queryParams.get('entityDisplayName');
    const analyticItem = queryParams.get('analyticItem');
    const analyses = queryParams.getAll('analyses');
    const nlpFields = JSON.parse(queryParams.get('nlpFields'));
    const metadataFields = JSON.parse(queryParams.get('metadataFields'));
    const showNlpData = queryParams.get('showNlpData') ?? 'true';
    const showMetadata = queryParams.get('showMetadata') ?? 'true';
    const dataSourceType = queryParams.get('dataSourceType') as DataSourceType;
    const analysisGroupId = queryParams.get('analysisGroupId');
    const isSocial: boolean = JSON.parse(queryParams.get('isSocial'));
    const loadPromises: [Promise<Project>, Promise<Dashboard>?] = [
      this.projectService.getProjectById(this.projectId, false),
    ];
    if (this.dashboardId !== 'new') {
      loadPromises.push(this.dashboardService.getDashboardById(this.dashboardId, this.projectId));
    }
    const promises = await Promise.all(loadPromises);

    const [project] = promises;
    let dashboard = promises[1];
    if (!dashboard) {
      const session = await this.authService.getSession();
      dashboard = this.dashboardService.loadCachedDashboard(
        this.dashboardId,
        this.projectId,
        session.info2.user_id
      );
    }

    if (dashboard.dashboard_version > 10) {
      this.pinningService.set(dashboard.payload.pins);
    }

    // Build data source with a defensive fallback if group id is missing
    let dataSource: { type: 'collection' | 'group'; id: string };

    switch (dataSourceType) {
      default:
        this.logger.warn(
          '[DocumentViewWindowComponent] analysisGroupId is undefined; falling back to collection endpoint',
          {
            dataSourceType,
            analysisGroupId,
            collectionName,
            documentId,
            projectId: this.projectId,
          }
        );
      case 'analysis':
        dataSource = { type: 'collection', id: collectionName };
        break;
      case 'group':
        dataSource = { type: 'group', id: analysisGroupId };
        break;
    }

    const document = await this.documentService.getById(
      dataSource,
      documentId,
      analyses,
      this.projectId,
      isSocial
    );

    const sentimentScale: number = dashboard.payload.sentimentScale;
    const sentimentDisplay: string = dashboard.payload.sentimentDisplay;

    this.options = {
      collectionName,
      dataSourceType,
      analysisGroupId,
      isSocial,
      page: { count: 1, items: [document] },
      pageIndex: 0,
      documentIndex: 0,
      limit: 1,
      documentId,
      projectId: this.projectId,
      entity,
      entityDisplayName,
      analyticItem,
      sentimentScale,
      sentimentDisplay,
      dashboardId: this.dashboardId,
      isDashboardLocked: dashboard.locked,
      sentimentRanges: {
        feature: project.polarities.feature[sentimentScale],
        document: project.polarities.document[sentimentScale],
      },
      nlpFields: nlpFields?.map((f) => ({ id: f, display: false })),
      showNlpData: showNlpData === 'true',
      metadataFields: metadataFields?.map((f) => ({ id: f, display: false })),
      showMetadata: showMetadata === 'true',
    };

    this.channelService.open(documentId, (msg) => {
      if (msg.type === 'dialog-highlight' && msg.entity) {
        this.options = { ...this.options, entity: msg.entity, analyticItem: msg.analyticItem };
      }
    });
  }

  onHighlightChange(highlight: { entity: string; analyticItem: string } | null): void {
    if (highlight == null) {
      return;
    }
    this.channelService.send({
      type: 'window-highlight',
      documentId: this.options?.documentId,
      ...highlight,
    });
  }

  ngOnDestroy(): void {
    this.channelService.close();
  }
}
